import { Box, Button, Container, Grid, Icon, Paper, TextField, Typography } from '@mui/material';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import React, { ChangeEvent, FormEvent } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { StorePicker } from 'app/components/store-picker';
import { Translate } from 'app/components/translate';
import { UserPicker } from 'app/components/user-picker';
import { SeamStatus } from 'app/constants/seam-status';
import { PriorityEnum } from 'app/enum/priority-enum';
import { makeBlankSeamRequest, SeamRequest } from 'app/model/seam-request';
import { User } from 'app/model/user';
import { crisRequestService } from 'app/services/cris-request.service';
import { seamRequestService } from 'app/services/seam-request.service';
import { RootState } from 'app/store';
import { RouteWithIdParam } from 'app/type';
import { ModelWithoutId } from 'app/type/model-with-id';
import { RoleAccessUtils } from 'app/utils/role-access-utils';

import { selectRequest } from './slice';
import { selectProfile, selectRoles } from '../auth/auth.slice';

interface SeamRequestState {
    data: ModelWithoutId<SeamRequest>;
}

interface RequestFormProps extends RouteWithIdParam {
    // redux props
    profile?: User;
    roles: Record<string, boolean>;

    // redux actions
    selectRequest: (request: SeamRequest) => void;
}

class RequestForm extends React.Component<RequestFormProps, SeamRequestState> {
    public constructor(props: Readonly<RequestFormProps> | RequestFormProps) {
        super(props);

        const dueDate = format(new Date(), 'yyyy-MM-dd');

        this.state = {
            data: makeBlankSeamRequest({
                crisRequest: {
                    id: '',
                    dueDate,
                    estimatedDelivery: dueDate,
                    currentPlace: SeamStatus.MIDMATCHING,
                    description: '',
                    dateCreated: '',
                    jiraLink: '',
                    storeData: [],
                    priority: PriorityEnum.P1,
                    taggerPriority: PriorityEnum.P1,
                },
                dueDate,
                ...RequestForm.makeAssignmentData(props.roles, props.profile),
            }),
        };

        // binding allows us to use this directly in template without doing '() => this.handleSubmit()'
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleChangeEvt = this.handleChangeEvt.bind(this);
    }

    public handleSubmit(evt: FormEvent) {
        evt.preventDefault();
        crisRequestService
            .saveForSeamOnly({
                ...this.state.data.crisRequest,
                description: `DIRECT SEAM REQUEST: ${this.state.data.description}`,
                owner: this.state.data.manager,
                dueDate: this.state.data.dueDate || this.state.data.crisRequest.dueDate,
                estimatedDelivery: this.state.data.dueDate,
            })
            .subscribe((value) => {
                this.handleChange('crisRequest', value); // update the cris request reference based on response
                this.handleChange('owner', this.state.data.tuner); // auto assign to pattern user on creation
                seamRequestService.save(this.state.data).subscribe((request) => {
                    this.props.selectRequest(request);
                    this.props.history.push(`/seam/request/${request.id}`);
                });
            });
    }

    public handleChangeEvt(evt: ChangeEvent<{ name?: string; value: unknown }>) {
        if (!evt.target.name) {
            throw new Error('Control must have name defined');
        }

        this.handleChange(evt.target.name, evt.target.value);
    }

    public handleChange(name: string, value: unknown) {
        this.setState({
            ...this.state,
            data: {
                ...this.state.data,
                [name]: value,
            },
        });
    }

    public render() {
        return (
            <Container maxWidth={false}>
                <h2>Create Request</h2>
                <form onSubmit={this.handleSubmit}>
                    <Box component={Paper} className='p-2 mb-2' bgcolor='info.light'>
                        <Grid container spacing={3}>
                            <Grid item xs={6}>
                                <Paper
                                    className='p-2'
                                    sx={this.state.data.correction ? { backgroundColor: 'background.default' } : {}}
                                    onClick={() => this.handleChange('correction', true)}
                                >
                                    {this.state.data.correction ? (
                                        <Icon fontSize='large' className='pull-right'>
                                            check_circle
                                        </Icon>
                                    ) : (
                                        ''
                                    )}
                                    <Typography variant='h3'>
                                        <Translate tkey='headings:Unmap' />
                                    </Typography>
                                    <Typography variant='body2'>
                                        Correct the current set of mid tuples by unmapping them from a store
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6}>
                                <Paper
                                    className='p-2'
                                    sx={this.state.data.correction ? {} : { backgroundColor: 'background.default' }}
                                    onClick={() => this.handleChange('correction', false)}
                                >
                                    {!this.state.data.correction ? (
                                        <Icon fontSize='large' className='pull-right'>
                                            check_circle
                                        </Icon>
                                    ) : (
                                        ''
                                    )}
                                    <Typography variant='h3'>
                                        <Translate tkey='headings:Map' />
                                    </Typography>
                                    <Typography variant='body2'>
                                        Discover all possible new mid tuples for a store and map them
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                            <StorePicker
                                value={this.state.data.store}
                                onChange={(event, store) =>
                                    this.setState({
                                        ...this.state,
                                        data: {
                                            ...this.state.data,
                                            store: store ? store : this.state.data.store,
                                            crisRequest: {
                                                ...this.state.data.crisRequest,
                                                brand: store ? store.brand : this.state.data.store.brand,
                                            },
                                        },
                                    })
                                }
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                fullWidth
                                name='dueDate'
                                label='Due Date'
                                type='date'
                                value={this.state.data.dueDate?.toString()}
                                onChange={this.handleChangeEvt}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={3}></Grid>
                        <Grid item xs={3}>
                            <UserPicker
                                label='Manager'
                                value={this.state.data.manager}
                                onChange={(evt, value) => this.handleChange('manager', value)}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <UserPicker
                                label='Pattern Creator'
                                value={this.state.data.tuner}
                                onChange={(evt, value) => this.handleChange('tuner', value)}
                            />
                        </Grid>
                        <Grid item xs={6}></Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                name='description'
                                label='Description'
                                multiline
                                rows={6}
                                value={this.state.data.description}
                                onChange={this.handleChangeEvt}
                            />
                        </Grid>
                        <Grid item xs={6}></Grid>
                        <Grid item xs={6}>
                            <Button type='submit' variant='contained' color='primary'>
                                Submit
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Container>
        );
    }

    public static getDerivedStateFromProps(props: Readonly<RequestFormProps>, state: SeamRequestState) {
        if (state.data.tuner || state.data.manager) {
            return state;
        }

        if (!props.profile) {
            return state;
        }

        return {
            data: {
                ...state.data,
                ...RequestForm.makeAssignmentData(props.roles, props.profile),
            },
        };
    }

    private static makeAssignmentData(roles: Record<string, boolean>, profile?: User) {
        return {
            manager: RoleAccessUtils.hasAnyRole(roles, ['ROLE_SEAM_MANAGER']) ? profile : undefined,
            tuner: profile,
        };
    }
}

const mapStateToProps = (state: RootState) => ({
    profile: selectProfile(state),
    roles: selectRoles(state),
});

const connected = connect(mapStateToProps, { selectRequest: selectRequest as ActionCreatorWithPayload<SeamRequest> })(
    withRouter(RequestForm),
);

export { connected as RequestForm };
